import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ShoppingCart, Leaf, Clock, Calendar, AlertCircle, ChevronLeft, ChevronRight, Coffee, Users, Plus, Bell, Flame, Star, ZoomIn, Heart, Shield, Wheat } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import Seo from '@/components/Seo';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import SideSelectionModal from '@/components/app/SideSelectionModal';
import NotificationBell from '@/components/app/NotificationBell';
import ReviewPrompt from '@/components/app/ReviewPrompt';

import { API_URL } from '@/config/api';
import { CUTOFF_HOUR, canStillOrderToday, isInGraceWindow, graceMinutesLeft, nextBusinessDay } from '@/lib/cutoff';

const DAY_LABELS = {
  'MON': 'Monday',
  'TUE': 'Tuesday',
  'WED': 'Wednesday',
  'THU': 'Thursday',
  'FRI': 'Friday'
};

const SHORT_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];

// Get today's day of week - moved outside component to prevent recreation
const getTodayDayOfWeek = () => {
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return days[new Date().getDay()];
};

// Check if a day has passed - moved outside component as pure function
const checkIsDayPast = (dayOfWeek, weeklyMenuData, preOrderStatusData) => {
  if (!weeklyMenuData) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dayData = weeklyMenuData.days?.find(d => d.day_of_week === dayOfWeek);
  if (!dayData?.date) return false;
  
  const dayDate = new Date(dayData.date);
  dayDate.setHours(0, 0, 0, 0);
  
  // If it's today, check cutoff time
  if (dayDate.getTime() === today.getTime()) {
    return preOrderStatusData && !preOrderStatusData.is_open;
  }
  
  return dayDate < today;
};

// Category display order priority
const CATEGORY_ORDER = ['light_meals', 'mains', 'pasta', 'sandwiches', 'salads', 'office_platters'];

// Canteen category labels (not main meal categories)
const CANTEEN_CATEGORIES = ['chips', 'chocolates', 'energy_drinks', 'juice', 'soft_drinks', 'sweets', 'water', 'snacks', 'drinks'];

// Format category name for display
const formatCategory = (cat) => {
  if (cat === 'all') return 'All';
  return cat.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// Normalize a category for comparisons (case/spacing agnostic — handles "CHIPS", "Energy Drinks", etc.)
const normCat = (c) => (c || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

// Friendly label for a meal-of-day date
const modDayLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Sort items by category priority, then alphabetically within category
const sortByCategory = (items) => {
  return [...items].sort((a, b) => {
    const aIdx = CATEGORY_ORDER.indexOf(a.category);
    const bIdx = CATEGORY_ORDER.indexOf(b.category);
    const aPriority = aIdx >= 0 ? aIdx : CATEGORY_ORDER.length;
    const bPriority = bIdx >= 0 ? bIdx : CATEGORY_ORDER.length;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return a.name.localeCompare(b.name);
  });
};

export default function AppMenu() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, openCart, itemCount, setDeliveryDate, total } = useCart();
  const { token, isAuthenticated, user } = useAuth();
  const [weeklyMenu, setWeeklyMenu] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [canteenItems, setCanteenItems] = useState([]);
  const [platterItems, setPlatterItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [preOrderStatus, setPreOrderStatus] = useState(null);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'all'
  const [activeSection, setActiveSection] = useState(
    CANTEEN_CATEGORIES.includes(normCat(searchParams.get('category'))) ? 'canteen' : 'meals'
  ); // 'meals', 'canteen', 'platters'
  
  // Side selection modal state
  const [sideSelectionItem, setSideSelectionItem] = useState(null);
  const [pendingDeliveryDate, setPendingDeliveryDate] = useState(null);
  const [todaysSpecials, setTodaysSpecials] = useState([]);
  const [mealOfDayList, setMealOfDayList] = useState([]);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [stockAvailability, setStockAvailability] = useState({});
  const [pendingReviewOrder, setPendingReviewOrder] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [canteenVisible, setCanteenVisible] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [vegOnly, setVegOnly] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [halaalOnly, setHalaalOnly] = useState(false);
  const [gfOnly, setGfOnly] = useState(false);
  const [itemRatings, setItemRatings] = useState({});
  
  // Ref to track if data has been fetched to prevent double-fetching
  const hasFetched = useRef(false);
  const gridRef = useRef(null);

  // Helper function using current state - wrapped in useCallback with stable deps
  const isDayPast = useCallback((dayOfWeek, weeklyMenuData) => {
    return checkIsDayPast(dayOfWeek, weeklyMenuData, preOrderStatus);
  }, [preOrderStatus]);

  // Fetch data only once on mount
  useEffect(() => {
    // Prevent double-fetch in StrictMode or re-renders
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const fetchData = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch all data with proper error handling
        const [weeklyRes, menuByTypeRes, catsRes, statusRes, specialsRes, stockRes, modRes] = await Promise.all([
          fetch(`${API_URL}/api/weekly-menu/current`),
          fetch(`${API_URL}/api/menu/by-type?date=${today}`),
          fetch(`${API_URL}/api/menu/categories`),
          fetch(`${API_URL}/api/pre-order/status`),
          fetch(`${API_URL}/api/menu/todays-specials`),
          fetch(`${API_URL}/api/menu/stock-availability`),
          fetch(`${API_URL}/api/menu/meal-of-day/upcoming?days=5`)
        ]);

        // Parse responses safely
        const weeklyData = weeklyRes.ok ? await weeklyRes.json() : { menu: null, days: [] };
        const menuByTypeData = menuByTypeRes.ok ? await menuByTypeRes.json() : { daily_meals: [], canteen: [], office_platters: [] };
        const catsData = catsRes.ok ? await catsRes.json() : [];
        const statusData = statusRes.ok ? await statusRes.json() : { is_open: true };
        const specialsData = specialsRes.ok ? await specialsRes.json() : [];
        const stockData = stockRes.ok ? await stockRes.json() : {};
        const modData = modRes.ok ? await modRes.json() : [];

        // Debug logging - only once
        console.log('[AppMenu] Data fetched:', {
          weeklyMenu: weeklyData.menu !== null,
          daily_meals: menuByTypeData.daily_meals?.length || 0,
          canteen: menuByTypeData.canteen?.length || 0,
          platters: menuByTypeData.office_platters?.length || 0
        });

        // Only set weeklyMenu if there's actual menu data
        const hasWeeklyMenu = weeklyData.menu !== null && weeklyData.days?.length > 0;
        
        // Batch state updates
        setWeeklyMenu(hasWeeklyMenu ? weeklyData : null);
        setMenuItems(menuByTypeData.daily_meals || []);
        setCanteenItems(menuByTypeData.canteen || []);
        setPlatterItems(menuByTypeData.office_platters || []);
        setCanteenVisible(menuByTypeData.canteen_visible !== false);
        setCategories(['all', ...catsData]);
        setPreOrderStatus(statusData);
        setTodaysSpecials(specialsData);
        setStockAvailability(stockData);
        setMealOfDayList(Array.isArray(modData) ? modData.filter(e => e.item) : []);
        
        // Auto-switch to 'all' view if no weekly menu is published
        if (!hasWeeklyMenu) {
          setViewMode('all');
        }

        // Set default day to today if it's a weekday
        const todayDay = getTodayDayOfWeek();
        if (weeklyData?.days && SHORT_DAYS.includes(todayDay)) {
          const todayData = weeklyData.days.find(d => d.day_of_week === todayDay);
          if (todayData && !checkIsDayPast(todayDay, weeklyData, statusData)) {
            setSelectedDay(todayDay);
          } else {
            // Find next available day
            const todayIndex = SHORT_DAYS.indexOf(todayDay);
            for (let i = todayIndex; i < SHORT_DAYS.length; i++) {
              if (!checkIsDayPast(SHORT_DAYS[i], weeklyData, statusData)) {
                setSelectedDay(SHORT_DAYS[i]);
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch only once on mount

  // Check for pending review when authenticated
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const checkReview = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/pending-review`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          if (data.order) {
            setPendingReviewOrder(data.order);
            setShowReview(true);
          }
        }
      } catch (e) { /* silent */ }
    };
    checkReview();
  }, [isAuthenticated, token]);

  // Get meals for selected day from weekly menu
  const getDayMeals = useCallback(() => {
    if (!weeklyMenu?.days || !selectedDay) return [];
    const dayData = weeklyMenu.days.find(d => d.day_of_week === selectedDay);
    return dayData?.meals || [];
  }, [weeklyMenu, selectedDay]);

  // Get date string for selected day
  const getSelectedDayDate = useCallback(() => {
    if (!weeklyMenu?.days || !selectedDay) return null;
    const dayData = weeklyMenu.days.find(d => d.day_of_week === selectedDay);
    return dayData?.date;
  }, [weeklyMenu, selectedDay]);

  // Categories for the active section — derived from the actual loaded items (data-driven,
  // so chips always match real items regardless of how categories were typed in BOH)
  const sectionCategories = useMemo(() => {
    if (activeSection === 'meals') {
      const catMap = new Map();
      menuItems.forEach(i => { if (i.category) catMap.set(normCat(i.category), i.category); });
      if (platterItems.length > 0) catMap.set('office_platters', 'office_platters');
      const mealCats = [...catMap.values()];
      mealCats.sort((a, b) => {
        const aIdx = CATEGORY_ORDER.indexOf(normCat(a));
        const bIdx = CATEGORY_ORDER.indexOf(normCat(b));
        const aPri = aIdx >= 0 ? aIdx : CATEGORY_ORDER.length;
        const bPri = bIdx >= 0 ? bIdx : CATEGORY_ORDER.length;
        return aPri - bPri || a.localeCompare(b);
      });
      return ['all', ...mealCats];
    }
    if (activeSection === 'canteen') {
      const catMap = new Map();
      canteenItems.forEach(i => { if (i.category) catMap.set(normCat(i.category), i.category); });
      return ['all', ...[...catMap.values()].sort((a, b) => a.localeCompare(b))];
    }
    return ['all'];
  }, [menuItems, canteenItems, activeSection, platterItems]);

  // If the selected category belongs to the other section (e.g. deep-link from Home), auto-switch tabs
  useEffect(() => {
    if (selectedCategory === 'all' || searchQuery) return;
    const sel = normCat(selectedCategory);
    if (sel === 'office_platters') return;
    const mealSet = new Set(menuItems.map(i => normCat(i.category)));
    const canteenSet = new Set(canteenItems.map(i => normCat(i.category)));
    if (activeSection === 'meals' && !mealSet.has(sel) && canteenSet.has(sel)) {
      setActiveSection('canteen');
    } else if (activeSection === 'canteen' && !canteenSet.has(sel) && mealSet.has(sel)) {
      setActiveSection('meals');
    }
  }, [selectedCategory, menuItems, canteenItems, activeSection, searchQuery]);

  // Smart search across ALL sections (meals + canteen + platters)
  const allSearchResults = useMemo(() => {
    if (!searchQuery) return null;
    const q = searchQuery.toLowerCase();
    const results = [];
    
    // Search meals
    menuItems.forEach(item => {
      if (item.name.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q)) {
        results.push({ ...item, _section: 'meals' });
      }
    });
    
    // Search canteen (only if visible)
    if (canteenVisible) {
      canteenItems.forEach(item => {
        if (item.name.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q)) {
          results.push({ ...item, _section: 'canteen' });
        }
      });
    }
    
    // Search platters
    platterItems.forEach(item => {
      if (item.name.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q)) {
        results.push({ ...item, _section: 'platters' });
      }
    });
    
    return results;
  }, [searchQuery, menuItems, canteenItems, platterItems, canteenVisible]);

  // Filter all menu items + platters (platters shown under Meals too)
  const filteredItems = sortByCategory([
    ...menuItems.filter(item => {
      return selectedCategory === 'all' || normCat(item.category) === normCat(selectedCategory);
    }),
    ...platterItems.filter(item => {
      return selectedCategory === 'all' || normCat(selectedCategory) === 'office_platters' || normCat(item.category) === normCat(selectedCategory);
    })
  ]);

  // Filter canteen items
  const filteredCanteenItems = canteenItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || normCat(item.category) === normCat(selectedCategory);
    return matchesCategory;
  });

  // Get items to display based on view mode
  const displayItems = useMemo(() => {
    if (viewMode === 'weekly' && weeklyMenu) {
      let dayMeals = getDayMeals();
      dayMeals = dayMeals.filter(item => {
        return selectedCategory === 'all' || normCat(item.category) === normCat(selectedCategory);
      });
      // Also include platters when on weekly view with 'all' or 'office_platters' selected
      if (selectedCategory === 'all' || normCat(selectedCategory) === 'office_platters') {
        dayMeals = [...dayMeals, ...platterItems];
      }
      return sortByCategory(dayMeals);
    }
    return filteredItems;
  }, [viewMode, weeklyMenu, filteredItems, getDayMeals, selectedCategory, platterItems]);

  // Favourites + dietary filters
  useEffect(() => {
    if (!token) { setFavorites(new Set()); return; }
    fetch(`${API_URL}/api/favorites`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(d => setFavorites(new Set((d || []).map(i => i.item_id)))).catch(() => {});
  }, [token]);

  // Per-item ratings (from order reviews)
  useEffect(() => {
    fetch(`${API_URL}/api/menu/ratings`).then(r => r.ok ? r.json() : {}).then(setItemRatings).catch(() => {});
  }, []);

  // Auto-apply the customer's saved dietary profile
  useEffect(() => {
    const prefs = user?.dietary_preferences || [];
    if (prefs.includes('vegetarian') || prefs.includes('vegan')) setVegOnly(true);
    if (prefs.includes('halaal')) setHalaalOnly(true);
    if (prefs.includes('gluten_free')) setGfOnly(true);
  }, [user]);

  const toggleFavorite = async (e, itemId) => {
    e.stopPropagation();
    if (!token) { toast.error('Sign in to save favourites'); return; }
    const isFav = favorites.has(itemId);
    const next = new Set(favorites);
    if (isFav) next.delete(itemId); else next.add(itemId);
    setFavorites(next);
    try {
      await fetch(`${API_URL}/api/favorites/${itemId}`, {
        method: isFav ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
    } catch {
      setFavorites(favorites); // revert on error
    }
  };

  const applyDietFav = (arr) => {
    let r = arr;
    if (vegOnly) r = r.filter(i => i.is_vegetarian || i.category === 'Vegetarian');
    if (halaalOnly) r = r.filter(i => i.is_halaal || (i.dietary_tags || []).includes('halaal'));
    if (gfOnly) r = r.filter(i => i.is_gluten_free || (i.dietary_tags || []).some(t => t === 'gluten_free' || t === 'gluten-free'));
    if (favOnly) r = r.filter(i => favorites.has(i.item_id));
    return r;
  };
  const visibleMeals = applyDietFav(displayItems);
  // Canteen items are packaged drinks/snacks — meal diet filters don't apply (only favourites)
  const visibleCanteen = favOnly ? filteredCanteenItems.filter(i => favorites.has(i.item_id)) : filteredCanteenItems;

  const handleAddToCart = (item, customDeliveryDate = null) => {
    // Ordering rules:
    // 1. Default to NEXT business day
    // 2. Same-day delivery allowed ONLY before 9am cutoff
    // 3. No ordering for past dates
    
    const now = new Date();
    const sameDayCutoffHour = CUTOFF_HOUR; // 9am cutoff (+10 min grace) for same-day orders
    
    // Helper to get next business day
    const getNextBusinessDay = () => {
      const t = nextBusinessDay(now);
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    };
    
    // Helper to check if same-day ordering is still allowed (before 9:10 — cut-off + grace)
    const canOrderSameDay = () => canStillOrderToday(now);
    
    // Determine delivery date based on section and context
    let deliveryDate = customDeliveryDate;
    
    if (activeSection === 'meals' && viewMode === 'weekly' && weeklyMenu) {
      // For daily meals in weekly view, use selected day's date
      deliveryDate = getSelectedDayDate();
      
      // Validate the selected date
      if (deliveryDate) {
        const selectedDate = new Date(deliveryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        
        // Check if it's today and past cutoff
        if (selectedDate.getTime() === today.getTime() && !canOrderSameDay()) {
          toast.error(`Today's ${sameDayCutoffHour}:00 AM cut-off has passed — your order was moved to the next delivery day.`);
          deliveryDate = getNextBusinessDay();
        }
      }
    } else if (activeSection === 'meals' && (viewMode === 'all' || !weeklyMenu)) {
      // For daily meals in full menu view, default to NEXT business day
      // Unless it's before 9am, then allow same-day
      if (canOrderSameDay()) {
        deliveryDate = deliveryDate || new Date().toISOString().split('T')[0];
      } else {
        deliveryDate = deliveryDate || getNextBusinessDay();
      }
    } else if (activeSection === 'canteen' || activeSection === 'platters') {
      // For canteen/platters - same rules
      if (canOrderSameDay()) {
        deliveryDate = deliveryDate || new Date().toISOString().split('T')[0];
      } else {
        deliveryDate = deliveryDate || getNextBusinessDay();
      }
    }

    // Meals always offer sides (configured, or optional paid add-ons); canteen & platters don't
    const isPlatter = item.menu_type === 'office_platters' || activeSection === 'platters';
    const isCanteen = item.menu_type === 'canteen' || activeSection === 'canteen';
    
    if (!isPlatter && !isCanteen) {
      // Close item detail modal and open side selection modal
      setSelectedItem(null);
      setSideSelectionItem(item);
      setPendingDeliveryDate(deliveryDate);
      return;
    }

    // No sides, add directly to cart
    addItemToCartDirectly(item, deliveryDate);
  };

  // Add item directly to cart (without sides modal)
  const addItemToCartDirectly = (item, deliveryDate) => {
    addItem({
      item_id: item.item_id || item.meal_id,
      name: item.name,
      price: item.default_price || item.price,
      image_url: item.image_url,
      delivery_date: deliveryDate,
      menu_type: item.menu_type || (activeSection === 'canteen' ? 'canteen' : activeSection === 'platters' ? 'office_platters' : 'daily_meals'),
      category: item.category,
      selected_sides: [],
      sides_total: 0
    });
    toast.success(`${item.name} added to cart`, { duration: 1500 });
    setSelectedItem(null);
  };

  // Add the meal of the day for a specific date
  const handleAddMealOfDay = (item, date) => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (date === todayStr && !canStillOrderToday()) {
      toast.error("Today's 9:00 AM cut-off has passed — your order can only be delivered from the next day. Pick an upcoming day instead.");
      return;
    }
    setSelectedItem(null);
    setSideSelectionItem(item);
    setPendingDeliveryDate(date);
  };

  // Handle adding item with sides from modal
  const handleAddWithSides = (itemWithSides) => {
    addItem({
      item_id: itemWithSides.item_id || itemWithSides.meal_id,
      name: itemWithSides.name,
      price: itemWithSides.default_price || itemWithSides.price,
      image_url: itemWithSides.image_url,
      delivery_date: itemWithSides.delivery_date,
      menu_type: itemWithSides.menu_type || (activeSection === 'canteen' ? 'canteen' : activeSection === 'platters' ? 'office_platters' : 'daily_meals'),
      category: itemWithSides.category,
      selected_sides: itemWithSides.selected_sides || [],
      sides_total: itemWithSides.sides_total || 0
    });
    
    const sidesCount = itemWithSides.selected_sides?.length || 0;
    const sidesText = sidesCount > 0 ? ` with ${sidesCount} side${sidesCount > 1 ? 's' : ''}` : '';
    toast.success(`${itemWithSides.name}${sidesText} added to cart`, { duration: 1500 });
    setSelectedItem(null);
  };

  // Check if selected day is past cutoff
  const isSelectedDayPast = selectedDay && isDayPast(selectedDay, weeklyMenu);

  // Calculate ordering info for cutoff alert
  const getOrderingInfo = () => {
    const now = new Date();
    const inGrace = isInGraceWindow(now);
    const canOrderSameDay = canStillOrderToday(now);
    
    // Countdown: to 9:00 while before cut-off, then through the 10-min grace window
    const minutesToNine = CUTOFF_HOUR * 60 - (now.getHours() * 60 + now.getMinutes());
    const minutesUntilCutoff = inGrace ? graceMinutesLeft(now) : Math.max(0, minutesToNine);
    
    // Delivery day: today while same-day is open, otherwise the next business day
    const deliveryDate = canOrderSameDay ? new Date(now) : nextBusinessDay(now);
    const fmt = (d) => d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'short' });
    
    return {
      canOrderSameDay,
      inGrace,
      minutesUntilCutoff,
      deliveryDate: fmt(deliveryDate),
      nextDeliveryDate: fmt(nextBusinessDay(now)),
      isToday: canOrderSameDay
    };
  };

  const orderingInfo = getOrderingInfo();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background"
      data-testid="app-menu"
    >
      <Seo title="Menu" path="/menu" description="Browse the full GC3H Online Canteen menu — daily meals, beverages, snacks and office platters. Filter by category, add your favourite sides, and order fresh food for pickup or delivery before the 9AM cut-off." />
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border-b border-border">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-heading text-2xl font-semibold text-foreground">Menu</h1>
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* Cart Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={openCart}
                className="relative p-3 bg-primary/10 rounded-2xl"
                data-testid="cart-btn"
              >
                <ShoppingCart className="h-6 w-6 text-primary" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </motion.button>
            </div>
          </div>

          {/* Ordering Cutoff Alert - Always Visible */}
          <div 
            className={`rounded-xl p-3 mb-4 ${
              orderingInfo.inGrace
                ? 'bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/40 dark:to-red-950/40 border border-orange-300 dark:border-orange-800'
                : orderingInfo.canOrderSameDay 
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40 border border-green-200 dark:border-green-800'
                : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border border-amber-200 dark:border-amber-800'
            }`}
            data-testid="ordering-cutoff-alert"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                orderingInfo.inGrace
                  ? 'bg-orange-100 dark:bg-orange-900/50'
                  : orderingInfo.canOrderSameDay
                  ? 'bg-green-100 dark:bg-green-900/50'
                  : 'bg-amber-100 dark:bg-amber-900/50'
              }`}>
                <Clock className={`h-5 w-5 ${
                  orderingInfo.inGrace ? 'text-orange-600 dark:text-orange-400' : orderingInfo.canOrderSameDay ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                }`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${
                    orderingInfo.inGrace ? 'text-orange-700 dark:text-orange-300' : orderingInfo.canOrderSameDay ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'
                  }`}>
                    {orderingInfo.inGrace ? '9AM Cut-off Passed — Grace Window' : orderingInfo.canOrderSameDay ? 'Same-Day Delivery Available!' : 'Ordering for the Next Delivery Day'}
                  </span>
                  {orderingInfo.canOrderSameDay && orderingInfo.minutesUntilCutoff <= 60 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse" data-testid="cutoff-countdown-badge">
                      {orderingInfo.minutesUntilCutoff} min left
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {orderingInfo.inGrace
                    ? `Only ${orderingInfo.minutesUntilCutoff} min of grace left to order for today — after that, delivery moves to ${orderingInfo.nextDeliveryDate}.`
                    : orderingInfo.canOrderSameDay 
                    ? `Order by 9:00 AM for delivery today. Otherwise, delivery on ${orderingInfo.nextDeliveryDate}`
                    : `Today's 9:00 AM cut-off has passed — your order will be delivered on ${orderingInfo.deliveryDate}`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          {weeklyMenu && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setViewMode('weekly')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  viewMode === 'weekly'
                    ? 'bg-primary text-white'
                    : 'bg-muted text-secondary-700'
                }`}
                data-testid="weekly-view-btn"
              >
                <Calendar className="h-4 w-4 inline mr-2" />
                Weekly Menu
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  viewMode === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-muted text-secondary-700'
                }`}
                data-testid="all-menu-btn"
              >
                Full Menu
              </button>
            </div>
          )}

          {/* Day Selector - Only show for weekly menu view */}
          {viewMode === 'weekly' && weeklyMenu && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Select Day</span>
                {preOrderStatus && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    Cutoff: {preOrderStatus.cutoff_time}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {SHORT_DAYS.map((day) => {
                  const isPast = isDayPast(day, weeklyMenu);
                  const isSelected = selectedDay === day;
                  const dayData = weeklyMenu.days?.find(d => d.day_of_week === day);
                  const date = dayData?.date ? new Date(dayData.date).getDate() : '';
                  
                  return (
                    <button
                      key={day}
                      onClick={() => !isPast && setSelectedDay(day)}
                      disabled={isPast}
                      className={`
                        flex-1 py-3 rounded-xl text-center transition-all relative
                        ${isSelected 
                          ? 'bg-primary text-white shadow-lg shadow-primary/30' 
                          : isPast 
                            ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' 
                            : 'bg-muted text-secondary-800 hover:bg-muted/80'
                        }
                      `}
                      data-testid={`day-${day}`}
                    >
                      <span className="font-semibold text-sm">{day}</span>
                      {date && <span className="block text-xs mt-0.5 opacity-70">{date}</span>}
                      {isPast && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/60 rounded-xl">
                          <X className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menu..."
              className="w-full h-12 pl-12 pr-4 bg-muted rounded-2xl border-none focus:ring-2 focus:ring-primary/20 transition-all"
              data-testid="menu-search"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* Dietary & favourites filters */}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => setVegOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${vegOnly ? 'bg-green-600 text-white' : 'bg-muted text-secondary-900'}`}
              data-testid="filter-veg"
            >
              <Leaf className="h-3.5 w-3.5" /> Vegetarian
            </button>
            <button
              onClick={() => setHalaalOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${halaalOnly ? 'bg-emerald-600 text-white' : 'bg-muted text-secondary-900'}`}
              data-testid="filter-halaal"
            >
              <Shield className="h-3.5 w-3.5" /> Halaal
            </button>
            <button
              onClick={() => setGfOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${gfOnly ? 'bg-amber-600 text-white' : 'bg-muted text-secondary-900'}`}
              data-testid="filter-gluten-free"
            >
              <Wheat className="h-3.5 w-3.5" /> Gluten-free
            </button>
            <button
              onClick={() => setFavOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${favOnly ? 'bg-primary text-white' : 'bg-muted text-secondary-900'}`}
              data-testid="filter-favorites"
            >
              <Heart className={`h-3.5 w-3.5 ${favOnly ? 'fill-white' : ''}`} /> Favourites
            </button>
          </div>
        </div>

        {/* Categories - Show for meals and canteen sections */}
        {(activeSection === 'meals' || activeSection === 'canteen') && sectionCategories.length > 1 && (
          <div className="px-4 pb-3">
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              {sectionCategories.map((cat) => (
                <motion.button
                  key={cat}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedCategory(cat);
                    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-muted text-secondary-700'
                  }`}
                  data-testid={`cat-${cat}`}
                >
                  {formatCategory(cat)}
                </motion.button>
              ))}
            </div>
          </div>
        )}
        
        {/* Section Toggle - Meals / Canteen / Platters - ALWAYS VISIBLE */}
        <div className="px-5 pb-3">
          <div className="flex gap-2 bg-muted p-1 rounded-xl">
            <button
              onClick={() => { setActiveSection('meals'); setSelectedCategory('all'); window.scrollTo(0, 0); }}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                activeSection === 'meals' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
              data-testid="tab-meals"
            >
              <Calendar className="h-3.5 w-3.5" />
              Meals &amp; Platters
            </button>
            {canteenVisible && (
              <button
                onClick={() => { setActiveSection('canteen'); setSelectedCategory('all'); window.scrollTo(0, 0); }}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  activeSection === 'canteen' ? 'bg-white shadow-sm text-blue-600' : 'text-muted-foreground'
                }`}
                data-testid="tab-canteen"
              >
                <Coffee className="h-3.5 w-3.5" />
                Canteen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Pre-order Cutoff Alert */}
      {activeSection === 'meals' && viewMode === 'weekly' && preOrderStatus && !preOrderStatus.is_open && getTodayDayOfWeek() !== 'SAT' && getTodayDayOfWeek() !== 'SUN' && (
        <div className="mx-5 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Pre-orders closed for today</p>
            <p className="text-xs text-amber-600 mt-0.5">{preOrderStatus.message}</p>
          </div>
        </div>
      )}

      {/* Menu Grid */}
      <div ref={gridRef} className="px-4 py-4 pb-24 max-w-6xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl animate-pulse bg-muted" style={{ height: 220 }} />
            ))}
          </div>
        ) : searchQuery && allSearchResults ? (
          /* ── Global Search Results ── */
          <>
            <div className="mb-3">
              <h2 className="font-heading text-lg font-bold text-secondary-900">Search Results</h2>
              <p className="text-xs text-muted-foreground">{allSearchResults.length} result{allSearchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;</p>
            </div>
            {allSearchResults.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {allSearchResults.map((item, idx) => {
                  const itemId = item.item_id || item.meal_id;
                  const itemPrice = item.default_price || item.price;
                  const sectionLabel = item._section === 'canteen' ? 'Canteen' : item._section === 'platters' ? 'Platter' : null;
                  const sectionColor = item._section === 'canteen' ? 'bg-blue-600' : item._section === 'platters' ? 'bg-purple-600' : null;
                  const stock = stockAvailability[itemId];
                  const isOutOfStock = stock && stock.in_stock === false;
                  return (
                    <motion.div key={`search-${itemId}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }}
                      className={`bg-white rounded-2xl overflow-hidden border border-border/60 shadow-sm ${isOutOfStock ? 'opacity-60' : ''}`}>
                      <div className="relative aspect-square cursor-pointer overflow-hidden" onClick={() => setSelectedItem(item)}>
                        <img src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'} alt={item.name} className={`w-full h-full object-cover ${isOutOfStock ? 'grayscale' : ''}`} />
                        {sectionLabel && <div className={`absolute top-2 right-2 ${sectionColor} px-2 py-0.5 rounded-full`}><span className="text-[10px] text-white font-medium">{sectionLabel}</span></div>}
                        {isOutOfStock && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">Sold Out</span></div>}
                        {!isOutOfStock && !sectionLabel && (
                          <div className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
                            {item.sides_config?.has_sides ? '+ Choose sides' : '+ Sides available'}
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <h4 className="font-semibold text-secondary-900 text-xs leading-tight line-clamp-2 min-h-[2rem] cursor-pointer" onClick={() => setSelectedItem(item)}>{item.name}</h4>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-base font-bold text-primary">R{itemPrice?.toFixed(0)}</span>
                          <motion.button whileTap={{ scale: 0.85 }} onClick={(e) => { e.stopPropagation(); if (!isOutOfStock) handleAddToCart(item); }} disabled={isOutOfStock}
                            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${isOutOfStock ? 'bg-gray-200 text-gray-400' : 'bg-primary text-white'}`}>
                            <Plus className="h-4 w-4" strokeWidth={3} />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground font-medium">No items found for &ldquo;{searchQuery}&rdquo;</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Try a different search term</p>
              </div>
            )}
          </>
        ) : activeSection === 'meals' ? (
          <>
            {/* Today's Specials - Horizontal Scroll (hide during search or category filter) */}
            {todaysSpecials.length > 0 && !searchQuery && selectedCategory === 'all' && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="h-5 w-5 text-orange-500" />
                  <h2 className="font-heading text-lg font-bold text-secondary-900">Today's Specials</h2>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {todaysSpecials.map((item, idx) => {
                    const itemPrice = item.default_price || item.price;
                    return (
                      <motion.div
                        key={item.item_id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex-shrink-0 w-[200px] snap-start bg-white rounded-2xl overflow-hidden shadow-md border border-border/50"
                        data-testid={`special-${item.item_id}`}
                      >
                        <div 
                          className="relative w-full aspect-square cursor-pointer overflow-hidden"
                          onClick={() => setSelectedItem(item)}
                        >
                          <img 
                            src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                          {item.is_special ? (
                            <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Flame className="h-2.5 w-2.5" fill="white" />
                              {item.special_note || 'Special'}
                            </div>
                          ) : item.is_featured ? (
                            <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star className="h-2.5 w-2.5" fill="white" />
                              New
                            </div>
                          ) : item.order_count > 0 ? (
                            <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star className="h-2.5 w-2.5" fill="white" />
                              Popular
                            </div>
                          ) : null}
                          <button
                            className="absolute top-2 right-2 w-6 h-6 bg-white/80 backdrop-blur rounded-full flex items-center justify-center"
                            onClick={(e) => { e.stopPropagation(); setFullscreenImage(item.image_url); }}
                          >
                            <ZoomIn className="h-3 w-3 text-slate-600" />
                          </button>
                        </div>
                        <div className="p-3">
                          <h4 className="font-semibold text-secondary-900 text-sm leading-tight line-clamp-1">{item.name}</h4>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-base font-bold text-primary">R{itemPrice?.toFixed(0)}</span>
                            <motion.button
                              whileTap={{ scale: 0.85 }}
                              onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                              className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-sm"
                              data-testid={`add-special-${item.item_id}`}
                            >
                              <Plus className="h-4 w-4" strokeWidth={3} />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Meal of the Day - next 5 days (set by BOH) */}
            {mealOfDayList.length > 0 && !searchQuery && selectedCategory === 'all' && (
              <div className="mb-6" data-testid="meal-of-day-section">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="h-5 w-5 text-amber-500" fill="currentColor" />
                  <h2 className="font-heading text-lg font-bold text-secondary-900">Meal of the Day</h2>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {mealOfDayList.map(({ date, item }, idx) => {
                    const itemPrice = item.default_price || item.price;
                    return (
                      <motion.div
                        key={date}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex-shrink-0 w-[220px] snap-start bg-white rounded-2xl overflow-hidden shadow-md border-2 border-amber-400/60"
                        data-testid={`meal-of-day-${date}`}
                      >
                        <div
                          className="relative w-full h-32 cursor-pointer overflow-hidden"
                          onClick={() => setSelectedItem(item)}
                        >
                          <img
                            src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Star className="h-2.5 w-2.5" fill="white" />
                            {modDayLabel(date)}
                          </div>
                        </div>
                        <div className="p-3">
                          <h4 className="font-semibold text-secondary-900 text-sm leading-tight line-clamp-1">{item.name}</h4>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-base font-bold text-primary">R{itemPrice?.toFixed(0)}</span>
                            <motion.button
                              whileTap={{ scale: 0.85 }}
                              onClick={(e) => { e.stopPropagation(); handleAddMealOfDay(item, date); }}
                              className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-sm"
                              data-testid={`add-meal-of-day-${date}`}
                            >
                              <Plus className="h-4 w-4" strokeWidth={3} />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Main Meals Grid */}
            {visibleMeals.length > 0 ? (
              <>
                {viewMode === 'weekly' && selectedDay && (
                  <div className="mb-3">
                    <h2 className="font-heading text-lg font-bold text-secondary-900">
                      {DAY_LABELS[selectedDay]} Menu
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {getSelectedDayDate()} &middot; {visibleMeals.length} items
                    </p>
                  </div>
                )}
                {!selectedDay && viewMode !== 'weekly' && (
                  <div className="mb-3">
                    <h2 className="font-heading text-lg font-bold text-secondary-900">All Meals</h2>
                    <p className="text-xs text-muted-foreground">{visibleMeals.length} items available</p>
                  </div>
                )}
                <div className="grid grid-cols-2 min-[480px]:grid-cols-2 gap-3">
                  {visibleMeals.map((item, idx) => {
                    const itemId = item.item_id || item.meal_id;
                    const itemPrice = item.default_price || item.price;
                    const stock = stockAvailability[itemId];
                    const isOutOfStock = stock && stock.in_stock === false;
                    const isLowStock = stock && stock.low_stock === true;
                    const dayPast = viewMode === 'weekly' && isDayPast(selectedDay, weeklyMenu);
                    const cantAdd = dayPast || isOutOfStock;
                    
                    return (
                      <motion.div
                        key={itemId}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        className={`bg-white rounded-2xl overflow-hidden border border-border/60 shadow-sm hover:shadow-md transition-shadow ${isOutOfStock ? 'opacity-60' : ''}`}
                        data-testid={`menu-item-${itemId}`}
                      >
                        {/* Image */}
                        <div 
                          className="relative aspect-square cursor-pointer overflow-hidden"
                          onClick={() => setSelectedItem(item)}
                        >
                          <img 
                            src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                            alt={item.name}
                            className={`w-full h-full object-cover transition-transform duration-200 hover:scale-105 ${isOutOfStock ? 'grayscale' : ''}`}
                          />
                          {isOutOfStock && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">Sold Out</span>
                            </div>
                          )}
                          {isLowStock && !isOutOfStock && (
                            <div className="absolute bottom-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                              Few left
                            </div>
                          )}
                          {(item.is_vegetarian || item.category === 'Vegetarian') && (
                            <div className="absolute top-2 left-2 bg-green-500 p-1 rounded-full">
                              <Leaf className="h-3 w-3 text-white" />
                            </div>
                          )}
                          <button
                            className="absolute top-2 right-10 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center"
                            onClick={(e) => { e.stopPropagation(); setFullscreenImage(item.image_url); }}
                          >
                            <ZoomIn className="h-3.5 w-3.5 text-white" />
                          </button>
                          <button
                            className="absolute top-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center"
                            onClick={(e) => toggleFavorite(e, itemId)}
                            data-testid={`fav-${itemId}`}
                            aria-label="Toggle favourite"
                          >
                            <Heart className={`h-3.5 w-3.5 ${favorites.has(itemId) ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                          </button>
                          {!isOutOfStock && (
                            <div className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
                              {item.sides_config?.has_sides ? '+ Choose sides' : '+ Sides available'}
                            </div>
                          )}
                        </div>
                        {/* Content */}
                        <div className="p-2.5">
                          <h4 
                            className="font-semibold text-secondary-900 text-xs leading-tight line-clamp-2 min-h-[2rem] cursor-pointer"
                            onClick={() => setSelectedItem(item)}
                          >
                            {item.name}
                          </h4>
                          {itemRatings[itemId]?.count > 0 && (
                            <div className="flex items-center gap-0.5 mt-0.5" data-testid={`rating-${itemId}`}>
                              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                              <span className="text-[11px] font-semibold text-secondary-900">{itemRatings[itemId].average}</span>
                              <span className="text-[10px] text-muted-foreground">({itemRatings[itemId].count})</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-base font-bold text-primary">R{itemPrice?.toFixed(0)}</span>
                            <motion.button
                              whileTap={{ scale: 0.85 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isOutOfStock) { toast.error(`${item.name} is sold out`); return; }
                                handleAddToCart(item);
                              }}
                              disabled={cantAdd}
                              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                                cantAdd
                                  ? 'bg-gray-200 text-gray-400'
                                  : 'bg-primary text-white active:bg-primary/80'
                              }`}
                              data-testid={`add-btn-${itemId}`}
                            >
                              <Plus className="h-4 w-4" strokeWidth={3} />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                {loading ? (
                  <p className="text-muted-foreground">Loading menu items...</p>
                ) : viewMode === 'weekly' && !weeklyMenu ? (
                  <>
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground font-medium">No weekly menu published yet</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Check back later or browse other sections</p>
                  </>
                ) : viewMode === 'weekly' && !selectedDay ? (
                  <p className="text-muted-foreground">Select a day to see available meals</p>
                ) : selectedCategory !== 'all' ? (
                  <>
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No meals in &ldquo;{formatCategory(selectedCategory)}&rdquo; category</p>
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="mt-4 px-6 py-2 bg-primary text-white rounded-xl text-sm font-medium"
                    >
                      View All Categories
                    </button>
                  </>
                ) : (
                  <>
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No meals available for {selectedDay ? DAY_LABELS[selectedDay] : 'today'}</p>
                  </>
                )}
              </div>
            )}
          </>
        ) : activeSection === 'canteen' && visibleCanteen.length > 0 ? (
          <>
            <div className="mb-3">
              <h2 className="font-heading text-lg font-bold text-secondary-900">Canteen Items</h2>
              <p className="text-xs text-muted-foreground">Always available &middot; {visibleCanteen.length} items</p>
            </div>
            <div className="grid grid-cols-2 min-[480px]:grid-cols-2 gap-3">
              {visibleCanteen.map((item, idx) => (
                <motion.div
                  key={item.item_id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.02 }}
                  className="bg-white rounded-2xl overflow-hidden border border-blue-100 shadow-sm"
                  data-testid={`canteen-item-${item.item_id}`}
                >
                  <div 
                    className="relative aspect-square cursor-pointer overflow-hidden"
                    onClick={() => setSelectedItem(item)}
                  >
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400'}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 bg-blue-600 px-2 py-0.5 rounded-full">
                      <span className="text-[10px] text-white font-medium">Canteen</span>
                    </div>
                    <button
                      className="absolute bottom-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center"
                      onClick={(e) => { e.stopPropagation(); setFullscreenImage(item.image_url); }}
                    >
                      <ZoomIn className="h-3.5 w-3.5 text-white" />
                    </button>
                  </div>
                  <div className="p-2.5">
                    <h4 
                      className="font-semibold text-secondary-900 text-xs leading-tight line-clamp-2 min-h-[2rem] cursor-pointer"
                      onClick={() => setSelectedItem(item)}
                    >
                      {item.name}
                    </h4>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-base font-bold text-blue-600">R{item.price?.toFixed(0)}</span>
                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                        className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-sm active:bg-blue-700"
                        data-testid={`add-canteen-${item.item_id}`}
                      >
                        <Plus className="h-4 w-4" strokeWidth={3} />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        ) : activeSection === 'canteen' ? (
          <div className="text-center py-20">
            <Coffee className="h-12 w-12 mx-auto text-blue-300 mb-4" />
            {canteenItems.length > 0 && selectedCategory !== 'all' ? (
              <>
                <p className="text-muted-foreground font-medium">No items in &ldquo;{formatCategory(selectedCategory)}&rdquo;</p>
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium"
                >
                  View All Canteen Items
                </button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground font-medium">No canteen items available</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Drinks, snacks & more coming soon!</p>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Item Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
              className="fixed inset-0 bg-black/60 z-[60]"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[60] bg-white rounded-t-3xl max-h-[85vh] overflow-hidden"
              data-testid="item-modal"
            >
              <div className="relative">
                <div className="overflow-hidden">
                  <img 
                    src={selectedItem.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800'}
                    alt={selectedItem.name}
                    className="w-full h-72 object-cover cursor-pointer"
                    onClick={() => setFullscreenImage(selectedItem.image_url)}
                  />
                </div>
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md"
                >
                  <X className="h-5 w-5" />
                </button>
                <button 
                  onClick={() => setFullscreenImage(selectedItem.image_url)}
                  className="absolute top-4 left-14 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md"
                >
                  <ZoomIn className="h-5 w-5 text-slate-600" />
                </button>
                {(selectedItem.is_vegetarian || selectedItem.category === 'Vegetarian') && (
                  <div className="absolute top-4 left-4 bg-green-500 px-3 py-1.5 rounded-full flex items-center gap-1">
                    <Leaf className="h-4 w-4 text-white" />
                    <span className="text-white text-sm font-medium">Vegetarian</span>
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <h2 className="font-heading text-2xl font-semibold text-secondary-900 mb-2">
                  {selectedItem.name}
                </h2>
                <p className="text-muted-foreground mb-4">{selectedItem.description}</p>
                
                {viewMode === 'weekly' && selectedDay && (
                  <div className="mb-4 p-3 bg-primary/5 rounded-xl">
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <Calendar className="h-4 w-4" />
                      <span>Delivery: {DAY_LABELS[selectedDay]}, {getSelectedDayDate()}</span>
                    </div>
                  </div>
                )}
                
                {selectedItem.allergens?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-secondary-900 mb-2">Allergens</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.allergens.map(a => (
                        <span key={a} className="bg-orange-100 text-orange-700 text-xs px-3 py-1 rounded-full">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-3xl font-bold text-primary">
                    R{(selectedItem.default_price || selectedItem.price)?.toFixed(2)}
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAddToCart(selectedItem)}
                    disabled={viewMode === 'weekly' && isSelectedDayPast}
                    className={`px-8 py-4 rounded-2xl font-semibold text-lg ${
                      viewMode === 'weekly' && isSelectedDayPast
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-primary text-white'
                    }`}
                    data-testid="add-to-cart-modal"
                  >
                    {viewMode === 'weekly' && isSelectedDayPast ? 'Not Available' : 'Add to Cart'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Side Selection Modal */}
      <SideSelectionModal
        isOpen={!!sideSelectionItem}
        onClose={() => {
          setSideSelectionItem(null);
          setPendingDeliveryDate(null);
        }}
        menuItem={sideSelectionItem}
        onAddToCart={handleAddWithSides}
        deliveryDate={pendingDeliveryDate}
      />

      {/* SEO / informational content — what's on the menu and how to order */}
      <section className="px-5 pt-10 pb-28 max-w-xl mx-auto w-full text-sm text-muted-foreground space-y-3" data-testid="menu-seo-content">
        <h2 className="text-base font-semibold text-secondary-900 dark:text-white">Explore today's canteen menu</h2>
        <p>
          Our menu is prepared fresh every weekday by the Gallagher Convention Centre kitchen in Midrand. Expect hearty
          chef-cooked mains like grilled brisket, butter chicken curry and beef stew from around R60, plus toasted
          sandwiches, pastas, salads and light meals from about R52. Most mains come with your choice of sides — pap,
          rice, chips, vegetables and more — and you can add extra sides to any meal at each side's own price.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Something for every taste</h3>
        <p>
          Use the filter chips to find vegetarian, halaal-friendly and gluten-free dishes in seconds, or tap the heart to
          save your favourites for one-tap reordering. Keep an eye on the golden <strong>Meal of the Day</strong> rail for
          the kitchen's daily pick, and the <strong>Chef's Specials</strong> for limited-time dishes you won't want to miss.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Canteen shop essentials</h3>
        <p>
          Beyond cooked meals, the Canteen tab stocks everyday essentials from as little as R10 — cold drinks, juices,
          energy drinks, bottled water, chips, chocolates and sweets — perfect for topping up your desk drawer or adding
          a drink to your lunch order.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Ordering tips</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Order before the <strong>9AM cut-off</strong> to enjoy your meal the same day.</li>
          <li>Collect free at the canteen with your QR code, or choose delivery to Midrand, Waterfall, Kyalami or Sunninghill.</li>
          <li>Pay instantly with your <strong>digital wallet</strong>, card, or redeem a gift voucher at checkout.</li>
          <li>Loyalty discounts apply automatically once you've unlocked a reward tier — no codes needed.</li>
        </ul>
        <h3 className="font-semibold text-secondary-900 dark:text-white">A menu that changes with the week</h3>
        <p>
          The weekly menu view shows you what the kitchen is cooking for each day ahead, so you can plan lunch for the
          whole week in one sitting. Many of our customers order Monday to Friday in a single checkout — each dish is
          simply delivered on the day you chose for it. If a dish sells out or the kitchen swaps something in, the app
          reflects it immediately, and your favourites list makes it effortless to reorder the meals you love most.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Feeding a meeting or an event?</h3>
        <p>
          Switch to the platters section for generous office platters — wraps, sandwiches, savouries and sweet treats —
          prepared for meetings, workshops, training days and celebrations at Gallagher Convention Centre and nearby
          offices. Order platters a day ahead and the kitchen will have everything ready and beautifully presented on
          time. For large or recurring corporate orders, message the team in the app for a tailored quote.
        </p>
      </section>

      {/* Persistent Basket Bar - Uber Eats style */}
      <AnimatePresence>
        {itemCount > 0 && !sideSelectionItem && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-20 left-4 right-4 z-[55]"
            data-testid="basket-bar"
          >
            <button
              onClick={openCart}
              className="w-full h-14 bg-primary text-white rounded-2xl flex items-center px-4 shadow-xl shadow-primary/40"
            >
              {/* Item count badge */}
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-black text-white">{itemCount}</span>
              </div>
              
              {/* Label */}
              <span className="flex-1 text-center font-bold text-base">View Basket</span>
              
              {/* Total */}
              <span className="font-black text-base flex-shrink-0">R{total.toFixed(2)}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Image Viewer */}
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
            onClick={() => setFullscreenImage(null)}
            data-testid="fullscreen-image"
          >
            <button 
              onClick={() => setFullscreenImage(null)}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur rounded-full flex items-center justify-center z-10"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <img
              src={fullscreenImage}
              alt="Full size"
              className="max-w-full max-h-full object-contain p-4"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Prompt */}
      {showReview && pendingReviewOrder && (
        <ReviewPrompt
          order={pendingReviewOrder}
          token={token}
          onClose={() => { setShowReview(false); setPendingReviewOrder(null); }}
          onSubmitted={() => setPendingReviewOrder(null)}
        />
      )}
    </motion.div>
  );
}
